import { selectDevice, selectSelectedAppType, selectSelectedMarket } from "../../state/selectors";
import { getState } from "../../state/store";

import { chartNames, deviceGroups, eventStatuses, fwuErrors, strCode } from "../../consts";
import { t } from "../../services";
import { arrayUtil, deviceUtil, locationUtil, objectUtil, rumEventsUtil } from "../../utils";

// FW Update Statistics
// _________________________________________________________________________
const getFwUpdateStatistics = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({ chartName: chartNames.FWU_STATISTICS });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices
  });

  if (!chartData?.grouped || !chartData?.dates || !metricsConfig) {
    return [];
  }

  const { grouped, dates } = chartData;

  const result = [];
  let totalFWUCount = 0;

  grouped?.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      const { successfulFWU, successfulNotReconnectedFWU, failedFWU, total } =
        rumEventsUtil.getFWUStatistics(events);

      totalFWUCount += total;

      result.push({
        date: day,
        metrics: {
          [metricsConfig.successful.id]: successfulFWU,
          [metricsConfig.successfulNotReconnected.id]: successfulNotReconnectedFWU,
          [metricsConfig.failed.id]: failedFWU
        }
      });
    });

  return { result, totalFWUCount };
};

const getFwCountByMarket = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.FWU_COUNT
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event.metadata?.market
  });

  if (!chartData?.grouped || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;

  let result = [];

  grouped.size > 0 &&
    grouped.forEach((events, market) => {
      const { successfulFWU, successfulNotReconnectedFWU, failedFWU } =
        rumEventsUtil.getFWUStatistics(events);

      result.push({
        market,
        metrics: {
          [metricsConfig.successful.id]: successfulFWU,
          [metricsConfig.successfulNotReconnected.id]: successfulNotReconnectedFWU,
          [metricsConfig.failed.id]: failedFWU
        }
      });
    });

  return result;
};

const getFwCountByAppType = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.FWU_COUNT
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event.appType
  });

  if (!chartData?.grouped || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;

  let result = [];

  grouped.size > 0 &&
    grouped.forEach((events, appType) => {
      const { successfulFWU, successfulNotReconnectedFWU, failedFWU } =
        rumEventsUtil.getFWUStatistics(events);

      result.push({
        appType,
        metrics: {
          [metricsConfig.successful.id]: successfulFWU,
          [metricsConfig.successfulNotReconnected.id]: successfulNotReconnectedFWU,
          [metricsConfig.failed.id]: failedFWU
        }
      });
    });

  return result;
};

const getFwCountByLocation = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.FWU_COUNT
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event.metadata?.store
  });

  if (!chartData?.grouped || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;

  const result = [];

  grouped.size > 0 &&
    grouped.forEach((events, location) => {
      const locationName = location
        ? locationUtil.getLocationNameById(location)
        : t(strCode.NO_LOCATION);

      if (locationName) {
        const { successfulFWU, successfulNotReconnectedFWU, failedFWU } =
          rumEventsUtil.getFWUStatistics(events);

        result.push({
          location: locationName,
          metrics: {
            [metricsConfig.successful.id]: successfulFWU,
            [metricsConfig.successfulNotReconnected.id]: successfulNotReconnectedFWU,
            [metricsConfig.failed.id]: failedFWU
          }
        });
      }
    });

  return result;
};

// FW Update Statistics Reports
// _________________________________________________________________________
const getFWStatisticsReport = (data, devices) => {
  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    isReportConfig: true
  });

  if (!chartData?.grouped) {
    return [];
  }

  const { grouped, dates } = chartData;

  let result = [];

  let successfulFWUTotal = 0;
  let successfulNotReconnectedFWUTotal = 0;
  let failedFWUTotal = 0;

  grouped.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      if (events?.length) {
        const { successfulFWU, successfulNotReconnectedFWU, failedFWU, total } =
          rumEventsUtil.getFWUStatistics(events);

        const stats = {
          ["Date"]: day,
          ["Successful updates"]: successfulFWU,
          ["Successful but not reconnected"]: successfulNotReconnectedFWU,
          ["Failed updates"]: failedFWU,
          ["Total"]: total
        };

        if (total) {
          successfulFWUTotal += successfulFWU;
          successfulNotReconnectedFWUTotal += successfulNotReconnectedFWU;
          failedFWUTotal += failedFWU;

          result.push(stats);
        }
      }
    });

  const total = successfulFWUTotal + successfulNotReconnectedFWUTotal + failedFWUTotal;
  const totals = {
    ["Successful updates"]: successfulFWUTotal,
    ["Successful but not reconnected"]: successfulNotReconnectedFWUTotal,
    ["Failed updates"]: failedFWUTotal,
    ["Total"]: total
  };

  result.push(totals);

  return result;
};

const getFWCountReport = (data, devices) => {
  const market = selectSelectedMarket()(getState());
  const appType = selectSelectedAppType()(getState());

  const isStatisticsByAppType = appType.length !== 1;
  const isStatisticsByMarket = !market || market.length !== 1;
  const isStatisticsByLocation = !isStatisticsByAppType && !isStatisticsByMarket;

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    isReportConfig: true
  });

  if (!chartData?.grouped) {
    return [];
  }

  const { grouped, dates } = chartData;

  let result = [];

  let successfulFWUTotal = 0;
  let successfulNotReconnectedFWUTotal = 0;
  let failedFWUTotal = 0;

  grouped.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      if (events?.length) {
        const groupFn = isStatisticsByAppType
          ? (event) => event.appType
          : isStatisticsByMarket
          ? (event) => event.metadata?.market
          : (event) => event.metadata?.store;

        const groupedByParam = objectUtil.groupBy(events, groupFn);

        groupedByParam?.size > 0 &&
          groupedByParam.forEach((events, param) => {
            const locationName =
              isStatisticsByLocation && param
                ? locationUtil.getLocationNameById(param)
                : t(strCode.NO_LOCATION);

            if (param || isStatisticsByLocation) {
              const { successfulFWU, successfulNotReconnectedFWU, failedFWU, total } =
                rumEventsUtil.getFWUStatistics(events);

              let stats = {};

              if (isStatisticsByAppType) {
                stats["App"] = param;
              } else if (isStatisticsByMarket) {
                stats["Market"] = param;
              } else {
                stats["Location"] = locationName;
              }

              stats = Object.assign(stats, {
                ["Date"]: day,
                ["Successful updates"]: successfulFWU,
                ["Successful but not reconnected"]: successfulNotReconnectedFWU,
                ["Failed updates"]: failedFWU,
                ["Total"]: total
              });

              if (total) {
                successfulFWUTotal += successfulFWU;
                successfulNotReconnectedFWUTotal += successfulNotReconnectedFWU;
                failedFWUTotal += failedFWU;

                result.push(stats);
              }
            }
          });
      }
    });

  const total = successfulFWUTotal + successfulNotReconnectedFWUTotal + failedFWUTotal;
  const totals = {
    ["Successful updates"]: successfulFWUTotal,
    ["Successful but not reconnected"]: successfulNotReconnectedFWUTotal,
    ["Failed updates"]: failedFWUTotal,
    ["Total"]: total
  };

  result.push(totals);

  return result;
};

// FW Update Performance
// _________________________________________________________________________
const getFwUpdatePerformance = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({ chartName: chartNames.FWU_PERFORMANCE });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices
  });

  if (!chartData?.grouped || !chartData?.dates || !metricsConfig) {
    return [];
  }

  const { grouped, dates } = chartData;

  const result = [];

  grouped?.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      let fwUpdates = 0;
      let avgSpeed = 0;
      let chargersAvgSpeed = 0;
      let holdersAvgSpeed = 0;

      if (events && events?.length > 0) {
        const chargerDurationsList = [];
        const holderDurationsList = [];

        events.forEach((event) => {
          let excludeEventFromStats = false;
          if (event?.eventDetails?.duration !== undefined && event?.eventDetails?.result?.status === eventStatuses.SUCCESS) {
            if (deviceGroups.chargers.includes(event.eventDetails.deviceTypeName)) {
              chargerDurationsList.push(event.eventDetails.duration);
            } else {
              holderDurationsList.push(event.eventDetails.duration);
            }
          } else {
            excludeEventFromStats = event?.eventDetails?.result?.error?.outerErrorCode == 32;
          }

          if (!excludeEventFromStats && event?.eventDetails?.result?.status === eventStatuses.SUCCESS) {
            fwUpdates += 1;
          }
        });

        const selectedDeviceFilter = selectDevice()(getState());
        const group = deviceUtil.getDeviceGroupByName(selectedDeviceFilter.value);

        if (group === "duoDevices") {
          chargersAvgSpeed = chargerDurationsList.length
            ? arrayUtil.getAverageValue(chargerDurationsList)
            : 0;

          holdersAvgSpeed = holderDurationsList.length
            ? arrayUtil.getAverageValue(holderDurationsList)
            : 0;

          avgSpeed = chargersAvgSpeed + holdersAvgSpeed;
        } else {
          avgSpeed =
            holderDurationsList.length || chargerDurationsList.length
              ? arrayUtil.getAverageValue([...holderDurationsList, ...chargerDurationsList])
              : 0;
        }
      }

      result.push({
        date: day,
        metrics: {
          [metricsConfig.fwUpdates.id]: fwUpdates,
          [metricsConfig.speed.id]: avgSpeed
        }
      });
    });

  return result;
};

// FW Update Errors
// _________________________________________________________________________
const getFwUpdateErrors = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({ chartName: chartNames.FWU_ERRORS });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    filterFn: (event) => {
      return (
        event?.eventDetails?.duration === undefined ||
        event?.eventDetails?.result?.status === eventStatuses.FAILED
      );
    }
  });

  if (!chartData?.grouped || !chartData?.dates || !metricsConfig) {
    return [];
  }

  const { grouped, dates } = chartData;

  const result = [];

  grouped?.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      const errorsCount = {};

      fwuErrors.forEach((error) => (errorsCount[error.id] = 0));
      errorsCount["other"] = 0;

      if (events && events?.length > 0) {
        events.forEach((event) => {
          const errorCode = event?.eventDetails?.result?.error?.outerErrorCode;
          const isSuccessButNotReconnected =
            rumEventsUtil.checkForSuccessButNotRecconectedFWU(event)?.isSuccessButNotReconnected;

          if (!isSuccessButNotReconnected) {
            if (errorCode) {
              const errorType = fwuErrors.find((error) => error.code == errorCode)?.id;

              if (errorType) {
                errorsCount[errorType] = errorsCount[errorType] + 1;
              } else {
                if (errorCode != 32) {
                  errorsCount["other"] = errorsCount["other"] + 1;
                }
              }
            } else {
              if (
                !event?.eventDetails?.duration ||
                (!event?.eventDetails?.result?.error &&
                  event?.eventDetails?.result?.status === eventStatuses.FAILED)
              ) {
                errorsCount["other"] = errorsCount["other"] + 1;
              }
            }
          }
        });
      }

      result.push({
        date: day,
        metrics: {
          [metricsConfig.general.id]: errorsCount.general,
          [metricsConfig.timeout.id]: errorsCount.timeout,
          [metricsConfig.cm.id]: errorsCount.cm,
          [metricsConfig.battery.id]: errorsCount.battery,
          [metricsConfig.other.id]: errorsCount.other
        }
      });
    });

  return result;
};

// FW Update Reconnection Time
// _________________________________________________________________________
const getReconnectionTimeDuringFWU = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.RECONNECTION_TIME_DURING_FWU
  });

  const deviceTypes = devices && devices[0] && deviceUtil.getDeviceTypesByName(devices[0]);

  const chartData = rumEventsUtil.prepareChartData({
    data,
    filterFn: (event) => {
      return deviceTypes ? deviceTypes.includes(event?.eventDetails?.deviceTypeName) : true;
    }
  });

  if (!chartData?.grouped || !chartData?.dates || !metricsConfig) {
    return [];
  }

  const { grouped, dates } = chartData;

  const result = [];

  const hasGroups = {
    monoDevices: false,
    chargers: false,
    holders: false
  };

  grouped?.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      let avgSpeedMono = 0;
      let avgSpeedChargers = 0;
      let avgSpeedHolders = 0;

      if (events && events?.length > 0) {
        const durationLists = {
          monoDevices: [],
          chargers: [],
          holders: []
        };

        events.forEach((event) => {
          const group = deviceUtil.getDeviceGroup(event?.eventDetails?.deviceTypeName);

          if (group && event?.eventDetails?.duration !== undefined) {
            durationLists[group].push(event.eventDetails.duration);
            hasGroups[group] = true;
          }
        });

        avgSpeedMono = durationLists.monoDevices?.length
          ? arrayUtil.getAverageValue(durationLists.monoDevices)
          : 0;

        avgSpeedChargers = durationLists.chargers?.length
          ? arrayUtil.getAverageValue(durationLists.chargers)
          : 0;

        avgSpeedHolders = durationLists.holders?.length
          ? arrayUtil.getAverageValue(durationLists.holders)
          : 0;
      }

      result.push({
        date: day,
        metrics: {
          [metricsConfig.mono.id]: avgSpeedMono,
          [metricsConfig.charger.id]: avgSpeedChargers,
          [metricsConfig.holder.id]: avgSpeedHolders
        }
      });
    });

  return rumEventsUtil.filterResults({ result, devices, hasGroups, metricsConfig });
};

const firmwareDashboardUtil = {
  // FW Update Statistics
  // _________________________________________________________________________
  getFwUpdateStatistics,
  getFwCountByMarket,
  getFwCountByAppType,
  getFwCountByLocation,

  // FW Update Statistics Reports
  // _________________________________________________________________________
  getFWStatisticsReport,
  getFWCountReport,

  // FW Update Performance
  // _________________________________________________________________________
  getFwUpdatePerformance,

  // FW Update Errors
  // _________________________________________________________________________
  getFwUpdateErrors,

  // FW Update Reconnection Time
  // _________________________________________________________________________
  getReconnectionTimeDuringFWU
};

export default firmwareDashboardUtil;
